/**
 * @typedef {import("../../generic/utilities/objectStructures/globalStructures").IdName} IdName IdName object
 * @typedef {import("../../generic/utilities/objectStructures/workflow/workflow").Workflow} Workflow Workflow object
 * @typedef {import("../../generic/utilities/objectStructures/workflow/workflow").Node} Node Node object
 * @typedef {import("../../generic/utilities/objectStructures/workflow/workflow").Position} Position Position object
 * @typedef {import("../../generic/utilities/objectStructures/workflow/workflow").NodeResult} NodeResult NodeResult object

 */

import getRandomUUID from '../../generic/utilities/randomuuid/RandomUUID'
import { formatCodeName } from '../../generic/utilities/objectStructures/codeNameFormat'
import { isPeriodicRuleComplete } from './periodicReturnRule'

/**
 * Method to create initial nodes
 * @param {number} idStartNode Start node id
 * @param {number} positionXStartNode Position x of start node
 * @param {number} positionYStartNode Position y of start node
 * @param {number} idEndNode End node id
 * @param {number} positionXEndNode Position x of end node
 * @param {number} positionYEndNode Position y of end node
 * @param {boolean} isConnectable Is connectable *Default true*
 * @returns {Node[]} node array: *Start and end nodes*
 */
const initialNodes = (idStartNode, positionXStartNode, positionYStartNode, idEndNode, positionXEndNode, positionYEndNode, isConnectable = true) => {
  return [{
    id: 'start',
    type: 'startNode',
    data: { label: 'Start', workflowPositionId: idStartNode, isConnectable },
    position: { x: positionXStartNode || 0, y: positionYStartNode || 0 },
    name: 'start',
    sourcePosition: 'right'
  },
  {
    id: 'end',
    type: 'endNode',
    data: { label: 'End', workflowPositionId: idEndNode, isConnectable },
    position: { x: positionXEndNode || 375, y: positionYEndNode || 0 },
    name: 'end',
    targetPosition: 'left'
  }]
}

/**
 * Method to create endEdge object
 * @param {Node} node Current job reference
 * @param {string} handle Source node type
 * @param {boolean} workflowInUse Workflow in use
 * @param {boolean} edgeCardVisible Variable to show/hide edge card. *Default true*
 * @param {function} onDeleteEdge Function to execute when an edge is deleted
 * @param {function} onUpdateEdge Function to execute when an edge is updated
 * @param {NodeResult} nodeResult NodeResult object
 * @returns {import("reactflow").Edge} endEdge object
 */
const endEdge = (id, node, handle, workflowInUse, edgeCardVisible = true, onDeleteEdge, onUpdateEdge, nodeResult) => {
  return {
    id,
    source: node.id,
    sourceHandle: handle,
    target: 'end',
    targetHandle: 'target',
    type: 'custom',
    data: {
      selected: false,
      type: handle,
      connectivity: 'FLEXIBLE',
      eventType: nodeResult.eventType,
      eventValue: nodeResult.eventValue,
      workflowInUse,
      edgeCardVisible,
      startDate: nodeResult.startDate,
      timezone: nodeResult.timezone,
      deleteEdge: onDeleteEdge,
      updateEdge: onUpdateEdge
    }
  }
}

/**
 * Method used to load node and edge list by given workflow
 * @param {object} props
 * @param {Workflow} props.workflow Workflow object
 * @param {function(Node, Position)} props.nodeData
 * @param {Boolean} props.edgeCardVisible=true Variable to show/hide edge card. *Default true*
 * @param {Boolean} props.isConnectable Variable to enable/disable connectable options. *Default true*
 * @param {number} props.maxJobIdNum Max job id count
 * @param {Boolean} props.addStartEndNodes Variable to enable/disable the creation of the starting and ending nodes
 * @param {Boolean} props.disableEdges Variable to enable/disable the edges
 * @param {function} props.onUpdateEdge Function to execute when an edge is updated
 * @param {function} props.onDeleteEdge Function to execute when an edge is deleted
 * @returns
 */
const getWorkflowNodesAndEdges = ({
  workflow,
  nodeData,
  edgeCardVisible = true,
  isConnectable = true,
  maxJobIdNum = 0,
  disableEdges = false,
  onUpdateEdge = () => {},
  onDeleteEdge = () => {}
}) => {
  const startPosition = workflow.workflowPositions.find((position) => position.startNode === true)
  const endPosition = workflow.workflowPositions.find((position) => position.endNode === true)
  const newNodes = [...initialNodes(startPosition?.id, startPosition?.positionX, startPosition?.positionY, endPosition?.id, endPosition?.positionX, endPosition?.positionY, isConnectable)]
  // Add nodes
  workflow.nodes.forEach((node) => {
    const position = workflow.workflowPositions.find((position) => position.nodeId === node.id)
    newNodes.push({
      id: node.id,
      type: node.nodeType === 'WORKFLOW' ? 'technologyNode' : 'technologyNode',
      // type: node.nodeType === 'WORKFLOW' ? 'subWorkflowNode' : 'technologyNode', //TODO: change to subWorkflowNode
      position: { x: position?.positionX, y: position?.positionY },
      data: nodeData(node, position)
    })
  })

  // Add edges
  const newEdges = []
  workflow.nodes.forEach((node) => {
    if (node.isFirst) {
      newEdges.push({
        id: getRandomUUID(),
        source: 'start',
        sourceHandle: 'start',
        target: node.id,
        targetHandle: 'target',
        type: 'custom',
        data: {
          selected: false,
          type: 'start',
          connectivity: 'FLEXIBLE',
          eventType: null,
          eventValue: null,
          edgeCardVisible: false,
          startDate: null,
          timezone: null,
          updateEdge: onUpdateEdge,
          deleteEdge: onDeleteEdge
        }
      })
    }

    node.nodeResults.forEach((nodeResult) => {
      const nextNode = workflow.nodes.find((node) => node.id === nodeResult.nextNode)
      if (!nextNode) {
        newEdges.push(endEdge(nodeResult.id, node, nodeResult.operationResult === 'OK' ? 'OK' : 'FAIL', workflow.workflowIsInUse, edgeCardVisible, onDeleteEdge, onUpdateEdge, nodeResult))
        return
      }

      const edge = {
        id: nodeResult.id,
        source: node.id,
        target: nextNode.id,
        sourceHandle: nodeResult.operationResult === 'OK' ? 'OK' : 'FAIL',
        targetHandle: 'target',
        type: 'custom',
        data: {
          connectivity: nodeResult.connectivity,
          eventType: nodeResult.eventType,
          eventValue: nodeResult.eventValue,
          selected: false,
          type: nodeResult.operationResult === 'OK' ? 'OK' : 'FAIL',
          edgeCardVisible,
          startDate: nodeResult.startDate,
          timezone: nodeResult.timezone,
          disableEdges,
          updateEdge: onUpdateEdge,
          deleteEdge: onDeleteEdge
        }
      }
      newEdges.push(edge)
    })
  })

  const returnValue = { nodes: newNodes, edges: newEdges, maxJobIdNum: newNodes.length + 1 }

  return returnValue
}

/**
 *
 * @param {import('reactflow').Node[]} nodes
 * @param {Workflow} workflow
 */
export const updatePositions = (nodes, workflow) => {
  const positions = []
  nodes.forEach((n) => {
    const node = workflow.nodes.find((node) => node.id === n.id)
    let position = workflow.workflowPositions.find((position) => position.nodeId === n.id || (n.id === 'start' && position.startNode) || (position.endNode && n.id === 'end'))
    if (!position) {
      position = {
        startNode: n.id === 'start',
        endNode: n.id === 'end',
        nodeId: node?.id
      }
    }
    position.positionX = n.position.x
    position.positionY = n.position.y
    positions.push(position)
  })
  return positions
}

// S7/S8/S34 (RN02.1/RN02.3/RN10.2): re-exported for backward compatibility — see
// `codeNameFormat.js` for the canonical implementation and its consumers.
export { formatCodeName }

/**
 * Historia 130 (S43, ajuste del PO 2026-09-15): el code del nodo que nace al arrastrar una operación
 * de la biblioteca.
 *
 * Se conserva el código de la operación tal cual —es lo que el usuario reconoce— y solo se le añade
 * un sufijo cuando ESE code ya está en el workflow, porque dentro de un workflow los codes de nodo
 * no se repiten: "Prueba", "Prueba-1", "Prueba-2"… El hueco se busca contra los codes que hay EN ESE
 * MOMENTO, así que borrar un nodo libera su code.
 *
 * @param {string} base El código de la operación arrastrada
 * @param {string[]} existingCodes Los codes de los nodos que ya tiene el workflow
 * @returns {string} Un code libre dentro de ese workflow
 */
export const uniqueOperationNodeCode = (base, existingCodes = []) => {
  const code = (base || '').trim()
  const taken = new Set((existingCodes || []).filter((existing) => typeof existing === 'string' && existing !== ''))
  if (code === '' || !taken.has(code)) {
    return code
  }
  let suffix = 1
  while (taken.has(code + '-' + suffix)) {
    suffix++
  }
  return code + '-' + suffix
}

export const validateWorkflow = (workflow, nodes, t_, frontendConfig) => {
  const minimumNextNodes = frontendConfig.workflowNodeSerialLinks === true ? 1 : 2
  if (!workflow || !workflow.nodes || workflow.nodes.length === 0) {
    return { isValid: false, message: t_('The subworkflow is empty or not properly defined') }
  }
  const nodeIds = nodes.map(n => n.id)
  const workflowNodes = workflow.nodes
  const hasStart = nodeIds.includes('start')
  const hasEnd = nodeIds.includes('end')
  if (!hasStart || !hasEnd) {
    return { isValid: false, message: t_('The subworkflow must contain both "start" and "end" nodes') }
  }

  const nodesWithInvalidResults = new Set()

  // Check if all nodes can be reached
  const reachableNodes = new Set()

  for (const node of workflowNodes) {
    const nodeResults = node.nodeResults || []

    nodeResults.forEach((item) => {
      if (item.nextNode) {
        reachableNodes.add(item.nextNode)
      }
    })

    if (node.isFirst) {
      reachableNodes.add(node.id)
    }

    if (nodeResults.length < minimumNextNodes) {
      nodesWithInvalidResults.add(node)
      continue
    }
    if (node.nodeResults.length > 2 && !frontendConfig.workflowNodeSerialLinks) {
      nodesWithInvalidResults.add(node)
      continue
    }
    const validNextNodes = nodeResults.filter(result => {
      const nextNode = result.nextNode === null ? 'end' : result.nextNode

      if (!nodeIds.includes(nextNode)) {
        return false
      }
      return true
    })

    if (validNextNodes.length < minimumNextNodes) {
      nodesWithInvalidResults.add(node)
    }
  }

  for (const node of workflowNodes) {
    if (!reachableNodes.has(node.id)) {
      nodesWithInvalidResults.add(node)
    }
  }

  if (nodesWithInvalidResults.size > 0) {
    // RN01.6: node.name is now the free/nullable descriptive field; the identifier is node.code.
    const disconnectedList = [...nodesWithInvalidResults].map(n => n.code || n.id).join(', ')
    return { isValid: false, message: t_('Some nodes are not properly connected:') + ' ' + disconnectedList }
  }

  // Story 80 (S42): a NEW external job with incomplete return timing is caught before calling the backend.
  if (findNewExternalJobMissingReturnTiming(workflowNodes)) {
    return { isValid: false, message: t_('An external job must define its return timing') }
  }

  return { isValid: true, message: 'OK' }
}

/**
 * Story 80 — the only case the client can decide on its own: a job that is being CREATED in this session
 * (no `jobReference.id` yet), is external, and whose return timing is missing or half-filled.
 *
 * Deliberately partial. Whether an already-persisted job needs its timing depends on how it is stored — a
 * job that has just become external must declare it, an untouched job older than the story must not — and the
 * client has no access to that state. Those cases are the server's, which answers with a 409 that the editor
 * paints as-is. Guessing here would either block saves that must succeed or promise a check that is not real.
 *
 * Shared by both save paths on purpose: the template editor via {@link validateWorkflow}, and the fabrication
 * order's workflow dialog, which saves through its own function and would otherwise run no client check at
 * all — which is precisely where a job can be externalised.
 *
 * @param {Array} workflowNodes nodes of the workflow being saved
 * @returns {Object|null} the first offending node, or null when there is none
 */
export const findNewExternalJobMissingReturnTiming = (workflowNodes) => {
  if (!workflowNodes) {
    return null
  }
  return workflowNodes.find((node) => {
    const jobReference = node?.jobReference
    if (!jobReference || !jobReference.external) {
      return false
    }
    // Already persisted: only the server can tell whether it must declare anything.
    if (jobReference.id) {
      return false
    }
    return !hasCompleteReturnTiming(jobReference.externalReturn)
  }) || null
}

const hasCompleteReturnTiming = (externalReturn) => {
  if (!externalReturn || !externalReturn.mode) {
    return false
  }
  if (externalReturn.mode === 'DATE') {
    return Boolean(externalReturn.date)
  }
  if (externalReturn.mode === 'DURATION') {
    return Boolean(externalReturn.duration)
  }
  if (externalReturn.mode === 'PERIODIC') {
    // Story 182 (C1): the rule must be complete; its shape is the frozen one of the contract.
    return isPeriodicRuleComplete(externalReturn.periodicRule)
  }
  // An unknown mode is not something the client rules on; the server rejects it with its own message.
  return true
}

export default getWorkflowNodesAndEdges
